import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Helmet } from 'react-helmet-async';

import { TotemInput } from 'components/TotemInput';
import { TotemPrimaryButton } from 'components/TotemPrimaryButton';

import { useSendResetPasswordMutation } from 'data/__generated__';

export const ForgotPassword = () => {
    const [emailAddress, setEmailAddress] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);

    const [sendResetPassword] = useSendResetPasswordMutation();

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setError(null);

        const { data } = await sendResetPassword({
            variables: { emailAddress, appName: 'totadmin' },
        });

        const result = data?.sendResetPassword;

        if (result?.success) {
            setSuccess(true);
        } else {
            setError(result?.error || 'Une erreur est survenue');
        }
    };

    if (success) {
        return (
            <Container>
                <Helmet title="Email envoyé · TOTADMIN" />
                <Title>Email envoyé</Title>
                <Message>Un email vous a été envoyé avec un lien pour réinitialiser votre mot de passe.</Message>
                <BackLink to="/">Retour à la connexion</BackLink>
            </Container>
        );
    }

    return (
        <Container>
            <Helmet title="Mot de passe oublié · TOTADMIN" />
            <Title>Mot de passe oublié</Title>
            <ForgotPasswordForm onSubmit={handleSubmit}>
                <TotemInput
                    value={emailAddress}
                    placeholder="Adresse email"
                    onChange={(value) => setEmailAddress(value)}
                    dataTest="forgot-password-email"
                />
                {error ? <ErrorMessage>{error}</ErrorMessage> : null}
                <SubmitButton type="submit" data-test="forgot-password-submit">
                    Envoyer
                </SubmitButton>
            </ForgotPasswordForm>
            <BackLink to="/">Retour à la connexion</BackLink>
        </Container>
    );
};

const Container = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background-color: ${({ theme }) => theme.backgroundColor};
`;

const Title = styled.h1`
    text-transform: uppercase;
    color: ${({ theme }) => theme.textColor};
    letter-spacing: 0.7px;
    font-size: 40px;
`;

const Message = styled.p`
    color: ${({ theme }) => theme.textColor};
    font-size: 16px;
    text-align: center;
    max-width: 400px;
`;

const ErrorMessage = styled.span`
    color: ${({ theme }) => theme.errorColor};
`;

const ForgotPasswordForm = styled.form`
    display: flex;
    flex-direction: column;
    width: 400px;

    & > :not(:first-child) {
        margin-top: 10px;
    }
`;

const SubmitButton = styled(TotemPrimaryButton)`
    margin-top: 15px;
`;

const BackLink = styled(Link)`
    color: ${({ theme }) => theme.ctaPrimaryTextColor};
    margin-top: 20px;
`;
